import { useMemo } from "react";
import { cloneDeep } from "lodash";
import { useGetDatapointsMetadata } from "api/ux/src";
import { addCustomFieldsAsCols } from "components/modal/portfolio-creation-edit/helper";
/**
 * Add custom fields to measures array
 * @param customFields - Array of custom field metadata to add
 * @param measures - Array of datapoint measures to enhance
 */
const addCustomFieldsToMeasures = (customFields, measures) => {
    customFields.forEach((customField) => {
        if (customField.metadata && customField.id) {
            const customDatapointId = customField.id;
            const isCustomDatapointExist = measures.some((m) => m.customDatapoint === customDatapointId);
            if (!isCustomDatapointExist && customDatapointId && customDatapointId.trim() !== "") {
                const customDatapoint = {
                    customDatapoint: customDatapointId,
                    name: customDatapointId.replace(/\./gu, ""),
                    periods: 1
                };
                measures.push(customDatapoint);
            }
        }
    });
};
/**
 * Hook to enhance cashflows grid widgets with custom fields (allocated amounts)
 * from Operation entity type. Detects cashflows widgets by checking for FundCashflowId dimension.
 * @param widgetId - The widget identifier
 * @param componentProps - The grid component props to enhance
 */
export const useCashflowsCustomFields = (widgetId, componentProps) => {
    // Detect if this is a cashflows widget by checking if it has FundCashflowId dimension
    const hasCashflowDimension = (param) => param.dimensions?.some((dim) => dim.standardDatapoint === "Fund.CashflowId" || dim.name === "FundCashflowId");
    // Detect investor-level cashflows by presence of Fund.InvestorId dimension alongside FundCashflowId
    const hasInvestorDimension = (param) => param.dimensions?.some((dim) => dim.standardDatapoint === "Fund.InvestorId");
    const isInvestorCashflowsWidget = componentProps?.datapointParams
        ?.some((param) => hasCashflowDimension(param) && hasInvestorDimension(param)) ?? false;
    // Get all datapoint metadata (includes both standard and custom fields)
    const [allMetadata] = useGetDatapointsMetadata();
    /*
     * Filter only Operation/Cashflows custom fields
     * Operation custom fields have path: ["Operation", "Cashflows", ...]
     */
    const customFields = useMemo(() => {
        if (!isInvestorCashflowsWidget || !allMetadata) {
            return [];
        }
        return allMetadata.filter((field) => {
            const path = field?.metadata?.path;
            return (field?.metadata?.isCustom?.toString() === "true" &&
                Array.isArray(path) &&
                path[0] === "Operation" &&
                path.includes("Cashflows"));
        });
    }, [isInvestorCashflowsWidget, allMetadata]);
    return useMemo(() => {
        // If not a cashflows widget or no custom fields, return original props
        if (!isInvestorCashflowsWidget || !customFields || customFields.length === 0) {
            return componentProps;
        }
        // Clone the component props to avoid mutating the original
        const enhancedProps = cloneDeep(componentProps);
        // Enhance columns template with custom fields
        if (enhancedProps.columnsTemplate?.columns) {
            const columns = enhancedProps.columnsTemplate.columns;
            addCustomFieldsAsCols(customFields, columns);
            enhancedProps.columnsTemplate.columns = columns;
        }
        // Enhance datapoint params with custom fields
        if (enhancedProps.datapointParams && enhancedProps.datapointParams.length > 0) {
            enhancedProps.datapointParams.forEach((param) => {
                if (param.measures && Array.isArray(param.measures)) {
                    const measures = param.measures;
                    addCustomFieldsToMeasures(customFields, measures);
                    param.measures = measures;
                }
            });
        }
        return enhancedProps;
    }, [isInvestorCashflowsWidget, customFields, componentProps]);
};
